// Dialog element → the element that had focus when it opened. Shared across
// dialogs so one can fall back to the opener of another that has closed since:
// dialog A closing itself and opening dialog B from the same click leaves B's
// opener inside the hidden A.
const openers = new WeakMap<HTMLElement, HTMLElement>();

// Bootstrap's own visibility test (`isVisible` in bootstrap/js/src/util):
// no client rects means display:none on it or an ancestor, and a closed
// off-canvas is only visibility:hidden. Either way focus() would not take.
function canTakeFocus(element: HTMLElement): boolean {
  return (
    element.isConnected &&
    element.getClientRects().length > 0 &&
    getComputedStyle(element).visibility === "visible" &&
    !element.matches(":disabled")
  );
}

function resolveFocusTarget(
  opener: HTMLElement,
  visitedDialogs: Set<HTMLElement> = new Set()
): HTMLElement | null {
  if (canTakeFocus(opener)) return opener;

  // A menu item that opened the dialog is hidden again once the dropdown
  // closes; its toggle is where a keyboard user continues. Same lookup
  // Bootstrap uses to find a menu's toggle.
  const toggle = opener
    .closest(".dropdown-menu")
    ?.parentElement?.querySelector<HTMLElement>("[data-bs-toggle='dropdown']");
  if (toggle && canTakeFocus(toggle)) return toggle;

  // Only a dialog that has closed hands over to its own opener: leaving one
  // that is still open would put focus behind its backdrop.
  const enclosingDialog = opener.closest<HTMLElement>(".modal, .offcanvas");
  if (
    !enclosingDialog ||
    enclosingDialog.classList.contains("show") ||
    visitedDialogs.has(enclosingDialog)
  ) {
    return null;
  }
  visitedDialogs.add(enclosingDialog);

  const enclosingOpener = openers.get(enclosingDialog);
  return enclosingOpener
    ? resolveFocusTarget(enclosingOpener, visitedDialogs)
    : null;
}

/**
 * Returns keyboard focus to whatever opened a Bootstrap-driven dialog.
 *
 * Bootstrap only does this for `data-bs-toggle` triggers; dialogs opened
 * through a prop never get it, so focus drops to the page body.
 *
 * Call `rememberOpener` when the dialog starts to show and `restoreFocus` once
 * it is hidden. A `v-if` teardown restores on its own. Focus is only moved
 * when it was actually lost (on the body or inside the dialog), so a caller
 * that places focus itself still wins. A hidden dropdown item falls back to
 * its toggle, an opener inside a since-closed dialog to that dialog's opener;
 * anything else leaves focus where it is.
 *
 * @example
 * ```ts
 * const { rememberOpener, restoreFocus } = useDialogFocusReturn(modalElementRef);
 * modalElementRef.value.addEventListener("show.bs.modal", rememberOpener);
 * modalElementRef.value.addEventListener("hidden.bs.modal", () => {
 *   restoreFocus();
 *   emit("hidden");
 * });
 * ```
 */
export function useDialogFocusReturn(dialogRef: Ref<HTMLElement | null>): {
  rememberOpener: () => void;
  restoreFocus: () => void;
} {
  // Kept outside the template ref: by `onUnmounted` the ref is already null.
  let dialog: HTMLElement | null = null;
  let opener: HTMLElement | null = null;

  const rememberOpener = (): void => {
    dialog = dialogRef.value;
    if (!dialog) return;

    const active = document.activeElement;
    opener =
      active instanceof HTMLElement &&
      active !== document.body &&
      !dialog.contains(active)
        ? active
        : null;

    if (opener) {
      openers.set(dialog, opener);
    } else {
      openers.delete(dialog);
    }
  };

  const restoreFocus = (): void => {
    if (!dialog || !opener) return;

    const active = document.activeElement;
    const focusIsLost =
      !active || active === document.body || dialog.contains(active);
    const target = focusIsLost ? resolveFocusTarget(opener) : null;
    opener = null;

    target?.focus();
  };

  onUnmounted(restoreFocus);

  return { rememberOpener, restoreFocus };
}
